import { Button, Chip, Division, Flex, Heading, Icon, Icons, Skeleton, Span, Markdown, Popover } from "@ir/ui";
import { ScoreCardData, ScoreCardTheme } from "@/store/analytics";
import classNames from "classnames";

export interface ScoreCardConfig {
  title: string;
  theme: ScoreCardTheme;
  icon?: Icons;
}

interface Props {
  config: ScoreCardConfig;
  data: ScoreCardData;
}

const THEME_MAP: Record<ScoreCardTheme, { primaryClass: string; iconBtnClass: string, bgColor: string, borderColor: string }> = {
  warning: { primaryClass: "text-warning-700", iconBtnClass: "bg-warning-50 text-warning-800", bgColor: "bg-warning-10", borderColor: "border-color-warning-50" },
  error: { primaryClass: "text-error-700", iconBtnClass: "bg-error-100 text-error-800", bgColor: "bg-error-10", borderColor: "border-color-error-100" },
  secondary: { primaryClass: "text-secondary-700", iconBtnClass: "bg-info-25 text-info-800", bgColor: "bg-info-10", borderColor: "border-color-info-25" },
  success: { primaryClass: "text-success-700", iconBtnClass: "bg-success-50 text-success-800", bgColor: "bg-success-10", borderColor: "border-color-success-100" },
};

const AnalyticsScoreCard = ({ config, data }: Props) => {
  const { title, theme, icon } = config;
  const { primaryValue, secondaryValue, chipLabel, footerText, trend, isLoading, error, tooltipMarkdown } = data;
  const { primaryClass, iconBtnClass, bgColor, borderColor } = THEME_MAP[theme];

  const cardClassNames = classNames("rounded-medium border-1 border-bottom-3 px-4 py-3 h-100", {
    "bg-white": error,
    [bgColor]: !error,
    "bg-error-25": error,
    [borderColor]: !error,
    "border-color-error-50": error,
  });

  const iconBtnClassNames = classNames(`p-2 ${error ? "text-error-800" : iconBtnClass}`, {
    "text-error-800": error,
    "text-info-800": !error,
  });

  const getFooterTextColor = () => {
    switch (trend) {
      case "UP":
        return "text-error-700";
      case "DOWN":
        return "text-success-700";
      default:
        return "text-grey-400";
    }
  };

  if (isLoading) {
    return (
      <Division className={cardClassNames}>
        <Flex align="start" justifyContent="space-between" className="mb-2">
          <Heading level={6} className="text-grey-500 font-bold m-0 text-uppercase">
            {title}
          </Heading>
          <Skeleton variant="rounded" width={30} height={30} />
        </Flex>
        <Heading level={4} className={`${error ? "text-grey-300" : primaryClass} font-bold mb-2`}>
          <Skeleton variant="rounded" width={100} height={16} radius={100} />
        </Heading>
        <Flex align="center" gap={0.5} className="mb-2">
          <Skeleton variant="rounded" width={60} height={16} radius={100} />
          <Skeleton variant="rounded" width={100} height={16} radius={100} />
        </Flex>
        <Skeleton variant="rounded" width={80} height={12} radius={100} />
      </Division>
    );
  }

  return (
    <Division className={cardClassNames}>
      <Flex align="start" justifyContent="space-between" className="mb-2">
        <Heading level={6} className="text-grey-500 font-bold m-0 text-uppercase">
          {title}
        </Heading>

        <Popover content={<Markdown content={tooltipMarkdown ?? ""} />} placement="top right" className="analytics-score-card-popover">
          <Button variant="solid" size="small" className={iconBtnClassNames}>
            {error && <Icon name={Icons.AlertTriangle} size="small" color={theme} />}
            {!error && icon && <Icon name={icon} size="small" color={theme} />}
          </Button>
        </Popover>

      </Flex>
      <Heading level={4} className={`${error ? "text-grey-300" : primaryClass} font-bold mb-2`}>
        {error && (
          <Span className="font-bold" style={{ letterSpacing: "-0.25rem" }}>
            ---
          </Span>
        )}
        {!error && primaryValue}
      </Heading>
      <Flex align="center" gap={0.5} className="m-0">
        {error && <Span className="text-grey-300 font-medium">{error.message}</Span>}
        {!error && <Span className="text-grey-800 font-bold">{secondaryValue}</Span>}
        {!error && chipLabel && <Chip borderless label={chipLabel} color={theme} size="medium" className="font-bold" />}
      </Flex>
      {error && (
        <Flex align="center" gap={0.5} className="text-error-700 font-normal mt-1">
          <Icon name={Icons.AlertCircle} size="small" /> Connection error
        </Flex>
      )}
      {!error && footerText && (
        <Flex
          align="center"
          gap={0.25}
          className={`font-regular mt-1 ${getFooterTextColor()}`}
        >
          {trend === "UP" && <Icon name={Icons.ArrowNarrowUp} size="small" className="text-error-700" />}
          {trend === "DOWN" && <Icon name={Icons.ArrowNarrowDown} size="small" className="text-success-700" />}
          {trend === "NEUTRAL" && <Icon name={Icons.ArrowRight} size="small" className="text-grey-400" />}
          {footerText}
        </Flex>
      )}
    </Division>
  );
};

export default AnalyticsScoreCard;
